import React, { ReactElement, useRef } from 'react';
import classNames from 'classnames';
import uniqueId from 'lodash/uniqueId';
import { Tooltip } from '../tooltips';

type Props = {
  checked?: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  disabled?: boolean;
  children?: ReactElement | ReactElement[];
  tooltip?: ReactElement;
  dark?: boolean;
  inverted?: boolean;
  icon?: string;
  labelPosition?: 'left' | 'right';
  'data-testid'?: string;
  'aria-label'?: string;
  toggleTooltipLabel?: string;
};

const ToggleSwitch = ({
  checked,
  onChange,
  disabled,
  className,
  children,
  tooltip,
  dark,
  inverted,
  icon,
  labelPosition,
  toggleTooltipLabel,
  ...rest
}: Props) => {
  const { current: toggleId } = useRef(uniqueId('toggle-'));

  const classes = classNames(className, 'toggle-switch', {
    disabled,
    dark,
    inverted,
    ...{ [`label-${labelPosition}`]: !!labelPosition && !!children },
  });

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!disabled) {
      onChange(!checked);
    }
  };

  const toggleButton = (
    <button
      id={`toggle-switch-${toggleId}-button`}
      className="toggle-switch-button custom-control custom-switch"
      type="button"
      aria-pressed={checked}
      onClick={handleToggle}
      disabled={disabled}
      data-testid={rest['data-testid']}
      aria-label={rest['aria-label']}
      {...(!!tooltip && { 'aria-describedby': `toggle-switch-${toggleId}-description` })}
    >
      {icon && <i className={`icon-${icon}`} />}
    </button>
  );

  return (
    <div className={classes} role="presentation" onClick={event => event.stopPropagation()}>
      <label className="d-flex toggle-switch-label-wrapper" htmlFor={`toggle-switch-${toggleId}-button`}>
        {!!children && <div className="toggle-switch-label">{children}</div>}

        {!!tooltip && (
          <div className="toggle-switch-tooltip" id={`toggle-switch-${toggleId}-description`}>
            {tooltip}
          </div>
        )}
      </label>

      {toggleTooltipLabel ? (
        <Tooltip className="custom-tooltip-v2" message={toggleTooltipLabel}>
          {toggleButton}
        </Tooltip>
      ) : (
        toggleButton
      )}
    </div>
  );
};

export default ToggleSwitch;
